import React, { useState, useEffect, FunctionComponent } from 'react'
import moment from 'moment'
import { isEmpty } from 'lodash'
import { Table, Button } from 'reactstrap'
// @ts-ignore
import Pagination from 'react-js-pagination'
import { useTranslation } from 'react-i18next'

import { countries } from 'Constants/countriesList'
import { DottedBtn } from 'Stories/DottedBtn/DottedBtn.stories'
import { WhiteGreenBtn, GreenBtn, RedBtn } from 'Stories/Button/Button.stories'
import arrowDown from 'Shared/img/arrow-down.svg'
import arrowUp from 'Shared/img/arrow-up.svg'
import { Curator, Payout, PayoutStatus } from 'Types/index'
import Toggle from './Toggle'

export interface AdminPanelProps {
  payouts: Array<Payout>
  payoutCounters: number
  activeTab: string
  curators: Array<Curator>
  getCuratorInfo: (curator_id: number) => void
  handleLoginAs: (curator_id: number, flag: boolean) => void
  handlePayoutConfirmation: (id: number) => void
  getItems: (page: number) => void
  setActiveTab: (page: number) => void
  setSortBy: (str: string) => void
  getCurators: () => void
  handleCuratorChangeAutopay: (curator_id: number, autopay: boolean) => void
  setSelectedCuratorId: (curatorId: number) => void
  setSelectedPayoutId: (payoutId: number) => void
  toggleInfoModal: () => void
  toggleCancelModal: () => void
  toggleConfirmModal: () => void
  handleSortAutopay: () => void
}

const AdminPanel: React.FC<AdminPanelProps> = ({
  payouts,
  activeTab,
  curators,
  payoutCounters,
  handleLoginAs,
  handlePayoutConfirmation,
  getItems,
  setActiveTab,
  handleCuratorChangeAutopay,
  toggleInfoModal,
  setSelectedCuratorId,
  setSelectedPayoutId,
  toggleCancelModal,
  handleSortAutopay,
}) => {
  const { t } = useTranslation()

  const [activePage, setActivePage] = useState<number>(1)

  useEffect(() => {
    getItems(activePage)
  }, [])

  // eslint-disable-next-line arrow-body-style
  const SortButton: FunctionComponent<{
    title: string
    onClick: () => void
  }> = ({ title, onClick }) => (
    <div className="sort-button__container">
      <button className="sort-button__button" type="button" onClick={onClick}>
        {title}
      </button>
      <div className="sort-button__arrows-wrapper">
        <img src={arrowUp} alt="" height="4" width="6" />
        <img src={arrowDown} alt="" height="4" width="6" />
      </div>
    </div>
  )

  const getCountryName = (code: string) => {
    const country_name = countries.find(country => country.code === code)
    if (country_name) return country_name.name
    return code
  }

  const handlePageChange = (pageNumber: number) => {
    setActivePage(pageNumber)
    setActiveTab(pageNumber)
    getItems(pageNumber)
  }

  const getPayoutsSum = (allPayouts: Payout[]) =>
    allPayouts.reduce((sum: number, payout: Payout) => {
      if (payout.status === 'paid') return sum + payout.amount
      return sum
    }, 0)

  const payoutsCountSinceAutopay = (payouts: Payout[], date: Date) => {
    const filteredPayouts = payouts.filter(payout =>
      moment(payout.date).isAfter(date)
    )
    return filteredPayouts.length
  }

  const onCuratorInfoClick = (id: number) => {
    toggleInfoModal()
    setSelectedCuratorId(id)
  }

  const handleCancelPayout = (id: number) => {
    setSelectedPayoutId(id)
    toggleCancelModal()
  }

  return (
    <>
      {!isEmpty(payouts) ? (
        <Table responsive striped>
          <thead>
            <tr>
              <th>Full name</th>
              <th>Email</th>
              {(activeTab === '1' || activeTab === '2') && (
                <>
                  <th>Amount</th>
                  <th>Bank country</th>
                  <th>Date</th>
                  <th>Total requests</th>
                </>
              )}
              {activeTab === '3' && (
                <>
                  <th>Total amount paid</th>
                  <th>Requests since autopayout</th>
                </>
              )}
              {activeTab === '2' && (
                <th>
                  <SortButton title="Autopayout" onClick={handleSortAutopay} />
                </th>
              )}
              {activeTab === '3' && <th>Autopayout</th>}
              {activeTab === '2' && <th />}
              <th />
            </tr>
          </thead>
          {activeTab === '3' && (
            <tbody>
              {curators &&
                curators.map(
                  ({
                    id,
                    first_name,
                    last_name,
                    email,
                    autopay,
                    autopay_date,
                    Payouts,
                  }) =>
                    autopay && (
                      <tr key={id}>
                        <td>
                          <DottedBtn
                            content={`${first_name} ${last_name || ''}`}
                            onClick={() => onCuratorInfoClick(id)}
                          />
                        </td>
                        <td>{email}</td>
                        <td>{getPayoutsSum(Payouts)}</td>
                        <td>
                          {autopay
                            ? payoutsCountSinceAutopay(Payouts, autopay_date)
                            : 0}
                        </td>
                        <td>
                          <Toggle
                            id={id}
                            isChecked={autopay}
                            onChange={() => {
                              handleCuratorChangeAutopay(id, autopay)
                            }}
                          />
                        </td>
                        <td className="users-table-td-buttons">
                          <Button
                            color="success"
                            size="sm"
                            className="login-as-btn"
                            onClick={() => handleLoginAs(id, false)}
                          >
                            Login As
                          </Button>
                        </td>
                      </tr>
                    )
                )}
            </tbody>
          )}

          {(activeTab === '1' || activeTab === '2') && (
            <tbody>
              {payouts.map(
                ({
                  id,
                  amount,
                  date,
                  status,
                  curator_id,
                  curator_payouts,
                  curator_complaints,
                  autopay,
                  Curator: {
                    user_id,
                    User: { first_name, last_name, email },
                    Recipient,
                  },
                }) => (
                  <tr key={id}>
                    <td>
                      <DottedBtn
                        content={`${first_name} ${last_name || ''}`}
                        onClick={() => onCuratorInfoClick(curator_id)}
                      />
                    </td>
                    <td>{email}</td>
                    <td>${amount}</td>
                    <td>
                      {Recipient &&
                        Recipient.country &&
                        getCountryName(Recipient.country)}
                    </td>
                    {date && <td>{moment(date).format('MM/DD/YYYY')}</td>}
                    <td>
                      {curator_payouts}
                      {curator_complaints ? (
                        <p style={{ fontSize: '10px' }}>
                          {curator_complaints} complaints since last payout
                        </p>
                      ) : (
                        ''
                      )}
                    </td>
                    {activeTab === '2' && (
                      <td>
                        {autopay ? (
                          <span className="payouts-table__on-label">
                            {t('adminPage.on')}
                          </span>
                        ) : (
                          t('adminPage.off')
                        )}
                      </td>
                    )}
                    <td className="users-table-td-buttons">
                      {activeTab === '1' && (
                        <WhiteGreenBtn
                          className="cancel-btn"
                          color="whiteGreen"
                          style={{ padding: ' 9px 12px' }}
                          onClick={() => {
                            handleCancelPayout(id)
                          }}
                        >
                          {t('adminPage.cancel')}
                        </WhiteGreenBtn>
                      )}

                      <GreenBtn
                        color="green"
                        style={{ padding: ' 5px 15px' }}
                        className="login-as-btn"
                        onClick={() => handleLoginAs(user_id, false)}
                      >
                        {t('adminPage.loginAs')}
                      </GreenBtn>
                      {status === PayoutStatus.Awaiting && (
                        <GreenBtn
                          className="login-as-btn"
                          color="green"
                          style={{ padding: ' 5px 15px' }}
                          onClick={() => handlePayoutConfirmation(id)}
                        >
                          {t('adminPage.pay')}
                        </GreenBtn>
                      )}
                      {status === PayoutStatus.Awaiting &&
                        Recipient.is_checking_needed && (
                          <RedBtn
                            className="login-as-btn"
                            color="red"
                            style={{ padding: ' 5px 15px' }}
                            onClick={() =>
                              // eslint-disable-next-line no-alert
                              alert('Awaiting for curators bank data updates.')
                            }
                          >
                            awaiting...
                          </RedBtn>
                        )}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          )}
        </Table>
      ) : (
        <p className="campaign-not-found">Nothing here</p>
      )}
      <div className="pagination-bottom">
        {
          <Pagination
            activePage={activePage}
            itemsCountPerPage={50}
            pageRangeDisplayed={5}
            totalItemsCount={payoutCounters}
            onChange={handlePageChange}
          />
        }
      </div>
    </>
  )
}

export default AdminPanel
